// `returnTo` validation for a broker-consumer worker (guardrail 5). The broker
// appends a one-time `?code=` to our callback URL whose `returnTo` we then
// honor as a redirect target — an unvalidated value is an open-redirect. We
// accept only a same-origin relative path, and as belt-and-suspenders also an
// ABSOLUTE https URL whose host is on the allowlist. Everything else collapses
// to the app's home path (`/` by default). Deliberately avoids the
// `new URL(x || fallback, base)` pattern, whose fallback can silently drop the
// path.
//
// The allowlist is the app's OWN host list, passed in: a host we hand a session
// cookie to is a host we can hand a redirect to, and the converse would be the
// odd position to hold. Restating it separately is what let corpus's copy name
// only a host this repo routes nowhere, so an absolute `returnTo` naming the
// host the user was really on collapsed to `/`.

import { matchesAllowedDomain } from "@bindery/shared/auth/domainAllowlist";
import { isSafeRelativePath } from "@bindery/shared/auth/returnPath";

export interface ReturnToGuard {
  /** The hosts an absolute `returnTo` may name. */
  readonly allowedHosts: readonly string[];
  /** Whether `host` is an allowed absolute-`returnTo` target. */
  hostAllowed: (host: string) => boolean;
  /**
   * Resolve a raw `returnTo` into a safe ABSOLUTE redirect URL string,
   * defaulting to `${origin}${homePath}` when absent or rejected. `origin` is the
   * (already host-gated) request origin, e.g. `https://retells.read-by-ear.com`.
   */
  resolveReturnTo: (raw: string | null, origin: string) => string;
  /**
   * Resolve a raw `returnTo` into a HOST-RELATIVE path, defaulting to
   * `homePath`.
   *
   * Used by `/auth/sign-in` (which hands the path to the callback URL it
   * builds) and `/auth/sign-out` (which re-attaches it to the host sign-out was
   * initiated on — the host whose cookie it just cleared).
   *
   * Reducing the value to a bare path is what makes both safe: a `returnTo` may
   * NOT choose the host here, even an allowlisted one, so an absolute URL is
   * accepted only to strip it down to its path. Returns a leading-slash path
   * only.
   */
  resolveReturnToPath: (raw: string | null) => string;
}

/** Bind the `returnTo` rules to one app's absolute-target allowlist. */
export function createReturnToGuard(
  allowedHosts: readonly string[],
  homePath = "/",
): ReturnToGuard {
  const hostAllowed = (host: string): boolean =>
    matchesAllowedDomain(host.toLowerCase(), allowedHosts);

  /** An absolute URL safe enough to reduce to a path or honor outright. */
  const acceptableAbsolute = (raw: string): URL | null => {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return null;
    }
    if (url.protocol !== "https:") return null;
    if (!hostAllowed(url.hostname)) return null;
    // Reject userinfo (phishing-heuristic bait: `user:pass@host`) and a
    // nonstandard port (never a legitimate target) even on an allowed host.
    if (url.username !== "" || url.password !== "" || url.port !== "") {
      return null;
    }
    return url;
  };

  return {
    allowedHosts,
    hostAllowed,
    resolveReturnTo: (raw, origin) => {
      const fallback = `${origin}${homePath}`;
      if (!raw) return fallback;
      if (raw.startsWith("/")) {
        return isSafeRelativePath(raw) ? `${origin}${raw}` : fallback;
      }
      const url = acceptableAbsolute(raw);
      return url === null ? fallback : url.toString();
    },
    resolveReturnToPath: (raw) => {
      if (!raw) return homePath;
      if (raw.startsWith("/")) return isSafeRelativePath(raw) ? raw : homePath;
      const url = acceptableAbsolute(raw);
      if (url === null) return homePath;
      const path = `${url.pathname}${url.search}`;
      return isSafeRelativePath(path) ? path : homePath;
    },
  };
}
